import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, ArrowRight, X, Sparkles } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Contextual Success Notification Component
 * 
 * Displays a celebratory notification after successful user actions,
 * with a suggested next step to guide the user through the app.
 * Automatically tracks views and interactions for analytics.
 */
const SuccessNotification = ({ 
  isOpen, 
  onClose, 
  title, 
  message, 
  nextStep,
  notificationType = 'UNKNOWN',
  autoCloseDelay = 8000 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [hasTracked, setHasTracked] = useState(false);

  // Track notification view
  const trackView = useCallback(async () => {
    if (hasTracked || !isOpen) return;
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API}/stats/notification-view`, {
          notification_type: notificationType,
          page: location.pathname,
          action_completed: true,
          next_step_clicked: false
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasTracked(true);
      }
    } catch (error) {
      // Silently fail - don't block UI for analytics
      console.debug('Analytics tracking failed:', error.message);
    }
  }, [hasTracked, isOpen, notificationType, location.pathname]);

  // Track next step click
  const trackNextStepClick = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.patch(`${API}/stats/notification-view/${notificationType}/next-step`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.debug('Analytics tracking failed:', error.message);
    }
  }, [notificationType]);

  useEffect(() => {
    if (isOpen) {
      // Small delay to trigger animation
      setTimeout(() => setIsVisible(true), 50);
      
      // Track the view
      trackView();
      
      // Auto close after delay
      const timer = setTimeout(() => {
        handleClose();
      }, autoCloseDelay);
      
      return () => clearTimeout(timer);
    } else {
      setHasTracked(false); // Reset tracking when closed
    }
  }, [isOpen, autoCloseDelay, trackView]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsExiting(false);
      onClose();
    }, 300);
  };

  const handleNextStep = async () => {
    // Track the click
    await trackNextStepClick();
    
    if (nextStep?.path) {
      handleClose();
      setTimeout(() => {
        navigate(nextStep.path);
      }, 350);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${isVisible && !isExiting ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleClose}
    >
      <div 
        className={`relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all duration-300 ${isVisible && !isExiting ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="success-notification"
      >
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
          data-testid="success-notification-close"
        >
          <X size={20} />
        </button>

        {/* Top gradient decoration */}
        <div className="h-2 bg-gradient-to-r from-[#00D3A8] via-[#00E5BB] to-[#00C7A1]" />

        {/* Content */}
        <div className="p-6 pt-5">
          {/* Success icon with animation */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-br from-[#00D3A8]/20 to-[#00E5BB]/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-9 h-9 text-[#00D3A8]" />
              </div>
              {/* Sparkle decorations */}
              <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-amber-400 animate-pulse" />
              <Sparkles className="absolute -bottom-1 -left-1 w-4 h-4 text-[#00D3A8] animate-pulse" style={{ animationDelay: '0.5s' }} />
            </div>
          </div>

          {/* Title */}
          <h3 
            className="text-xl font-bold text-gray-800 text-center mb-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="success-notification-title"
          >
            {title}
          </h3>

          {/* Message */}
          <p className="text-gray-600 text-center text-sm mb-5">
            {message}
          </p>

          {/* Next step suggestion */}
          {nextStep && (
            <div className="bg-gradient-to-r from-[#00D3A8]/5 to-[#00E5BB]/5 rounded-xl p-4 border border-[#00D3A8]/10">
              <p className="text-xs font-semibold text-[#00D3A8] uppercase tracking-wider mb-1.5">
                Prochaine étape suggérée
              </p>
              <p className="text-gray-700 font-medium text-sm mb-3">
                {nextStep.description}
              </p>
              <button
                onClick={handleNextStep}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] text-white py-2.5 px-4 rounded-lg font-medium text-sm hover:shadow-lg hover:shadow-[#00D3A8]/20 transition-all duration-200"
                data-testid="success-notification-next-step"
              >
                {nextStep.buttonText}
                <ArrowRight size={16} />
              </button>
            </div>
          )}

          {/* Alternative action */}
          <button
            onClick={handleClose}
            className="w-full text-gray-500 hover:text-gray-700 py-2.5 mt-3 text-sm font-medium transition-colors"
          >
            Continuer sur cette page
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Predefined success configurations for common actions
 */
export const SUCCESS_CONFIGS = {
  // Assemblée Générale created
  AG_CREATED: {
    title: 'Assemblée créée avec succès !',
    message: 'Votre assemblée générale a été planifiée. Les coproprietaires pourront bientôt confirmer leur participation.',
    nextStep: {
      description: 'Ajoutez les résolutions à voter lors de cette assemblée.',
      buttonText: 'Ajouter des résolutions',
      path: null // Will be set dynamically with AG id
    }
  },

  // Copropriétaire added
  COPRO_ADDED: {
    title: 'Copropriétaire ajouté !',
    message: 'Le nouveau coproprietaire a été enregistré. Un compte lui sera créé avec accès à l\'espace résident.',
    nextStep: {
      description: 'Continuez à ajouter d\'autres coproprietaires ou assignez un lot.',
      buttonText: 'Voir les coproprietaires',
      path: '/coproprietaires'
    }
  },

  // Building created
  BUILDING_CREATED: {
    title: 'Immeuble créé !',
    message: 'Votre nouvel immeuble a été ajouté avec succès à votre portefeuille.',
    nextStep: {
      description: 'Ajoutez maintenant les coproprietaires de cet immeuble.',
      buttonText: 'Ajouter des coproprietaires',
      path: '/coproprietaires'
    }
  },

  // Incident created
  INCIDENT_CREATED: {
    title: 'Incident signalé !',
    message: 'Votre signalement a bien été enregistré. Le gestionnaire sera notifié.',
    nextStep: {
      description: 'Suivez l\'avancement de votre signalement dans la liste des incidents.',
      buttonText: 'Voir mes incidents',
      path: '/incidents'
    }
  },

  // Incident resolved
  INCIDENT_RESOLVED: {
    title: 'Incident résolu !',
    message: 'L\'incident a été marqué comme résolu. Merci pour votre suivi.',
    nextStep: {
      description: 'Consultez les autres incidents en cours.',
      buttonText: 'Voir tous les incidents',
      path: '/incidents'
    }
  },

  // Resolution added
  RESOLUTION_ADDED: {
    title: 'Résolution ajoutée !',
    message: 'La résolution a été ajoutée à l\'ordre du jour de l\'assemblée.',
    nextStep: {
      description: 'Continuez à préparer l\'assemblée en ajoutant d\'autres résolutions.',
      buttonText: 'Ajouter une résolution',
      path: null
    }
  },

  // Team member added
  MEMBER_ADDED: {
    title: 'Membre ajouté à l\'équipe !',
    message: 'Le nouveau collaborateur a été ajouté. Un email d\'invitation lui sera envoyé.',
    nextStep: {
      description: 'Assignez des immeubles à ce nouveau membre.',
      buttonText: 'Gérer les assignations',
      path: '/equipe'
    }
  },

  // Event created
  EVENT_CREATED: {
    title: 'Événement créé !',
    message: 'L\'événement a été planifié et sera visible par les résidents concernés.',
    nextStep: {
      description: 'Publiez une annonce pour informer les résidents.',
      buttonText: 'Créer une annonce',
      path: '/annonces'
    }
  },

  // Payment successful
  PAYMENT_SUCCESS: {
    title: 'Paiement effectué !',
    message: 'Votre paiement a été traité avec succès. Un reçu vous sera envoyé par email.',
    nextStep: {
      description: 'Consultez vos factures et historique de paiements.',
      buttonText: 'Voir mes factures',
      path: '/mes-factures'
    }
  },

  // Profile updated
  PROFILE_UPDATED: {
    title: 'Profil mis à jour !',
    message: 'Vos informations ont été enregistrées avec succès.',
    nextStep: null
  },

  // Document uploaded
  DOCUMENT_UPLOADED: {
    title: 'Document ajouté !',
    message: 'Le document a été téléversé et est maintenant accessible.',
    nextStep: {
      description: 'Consultez tous les documents de cette assemblée.',
      buttonText: 'Voir les documents',
      path: null
    }
  },

  // Exercice comptable created
  EXERCICE_CREATED: {
    title: 'Exercice comptable créé !',
    message: 'Le nouvel exercice a été initialisé. Vous pouvez maintenant gérer la comptabilité.',
    nextStep: {
      description: 'Configurez le budget prévisionnel pour cet exercice.',
      buttonText: 'Configurer le budget',
      path: '/comptabilite'
    }
  },

  // AG closed successfully
  AG_CLOSED: {
    title: 'Assemblée clôturée avec succès !',
    message: 'Le procès-verbal a été généré automatiquement. Tous les votes ont été enregistrés.',
    nextStep: {
      description: 'Téléchargez le procès-verbal pour l\'archiver ou l\'envoyer aux coproprietaires.',
      buttonText: 'Télécharger le PV',
      path: null // Handled by download function
    }
  },

  // CSV Import - Copropriétaires
  IMPORT_COPRO_SUCCESS: {
    title: 'Import réussi !',
    message: 'Les coproprietaires ont été importés avec succès depuis votre fichier CSV.',
    nextStep: {
      description: 'Vérifiez que toutes les données ont été correctement importées.',
      buttonText: 'Voir les coproprietaires',
      path: '/coproprietaires'
    }
  },

  // CSV Import - Immeubles
  IMPORT_BUILDING_SUCCESS: {
    title: 'Import réussi !',
    message: 'Les immeubles ont été importés avec succès depuis votre fichier CSV.',
    nextStep: {
      description: 'Ajoutez maintenant les coproprietaires pour chaque immeuble.',
      buttonText: 'Ajouter des coproprietaires',
      path: '/coproprietaires'
    }
  }
};

export default SuccessNotification;
